/**
 * General Task design tokens — the one source of truth.
 *
 * Every color, radius, duration and hit size in the app should resolve to a name here, by role rather
 * than by hue ("text.tertiary", not "gray"). `colors.ts` keeps its old export names as aliases of these
 * so existing call sites keep working; `globals.css` mirrors them as CSS variables for Tailwind.
 * `tokens.test.ts` fails if the three drift apart.
 *
 * Rules the tokens encode:
 *   - Color carries meaning. Neutrals do the work; an intent color appears only when a state needs a look.
 *   - Past-due is `intent.warning` (amber), never red. Red (`intent.danger`) is for destructive actions and errors.
 *   - Every text token passes WCAG AA (4.5:1) on `surface.default`; see the ratios beside each value.
 *   - A glyph's size is not its hit area: interactive targets are `hit.touch` on phones.
 *   - Motion is short and eases out; `prefers-reduced-motion` removes it (see globals.css).
 */

export const tokens = {
    text: {
        primary: '#18181B', // 17.7:1 — titles, task names
        secondary: '#3F3F46', // 10.4:1 — body copy, control labels
        tertiary: '#71717A', // 4.8:1 (4.4:1 on surface.page and surface.sunken — use secondary there) — metadata, icons at rest
        inverse: '#FFFFFF',
    },
    surface: {
        page: '#F7F5F2', // warm tone behind the app shell, landing and auth screens; also the modal veil
        default: '#FFFFFF', // content areas, lists, dialogs
        raised: '#FDFDFD', // cards, list rows
        panel: '#FAFAFA', // side panels, details column
        sunken: '#F4F4F5', // inputs, insets, hover wells
    },
    line: {
        subtle: '#F0F0F1', // row dividers, list/details seam
        default: '#E4E4E7', // card and input borders
        strong: '#D4D4D8', // pressed/active outlines
    },
    intent: {
        brand: '#DB2979', // GT pink — brand moments, selected-row bar, now-line
        success: '#15803D', // 5.0:1 — done states, positive toasts
        warning: '#B45309', // 5.0:1 — past-due dates, "needs a look"
        danger: '#BE0A16', // 6.5:1 — destructive actions, errors
        info: '#2F6E9F', // 5.5:1 — links, informational badges
    },
    focus: {
        ring: '#18181B',
        width: '2px',
        offset: '2px',
    },
    radius: {
        xs: '4px', // chips, checkboxes
        sm: '6px', // buttons, inputs
        md: '10px', // cards, menus
        lg: '14px', // dialogs, sheets
        pill: '999px',
    },
    motion: {
        fast: '150ms', // hover, press
        base: '220ms', // entrances, popovers
        slow: '320ms', // sheets, page transitions
        complete: '320ms', // task-complete: the row fades, then closes
        easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)', // things arriving
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)', // things leaving
    },
    hit: {
        touch: '44px', // Apple HIG minimum on phones
        pointer: '24px', // minimum on desktop
    },
} as const

export type TTokens = typeof tokens
